import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, EmptyState } from '@/components/ui-bits';
import { Shield, Plus, X, Save, Trash2, Mail, RefreshCw, Copy, Clock } from 'lucide-react';
import { ROLE_LABELS, ROLE_BADGE_CLASS } from '@/lib/roles';
import { fmtRelative } from '@/lib/format';
import { toast } from 'sonner';

const ROLES = ['admin', 'supervisor', 'ventas', 'soporte', 'lectura'];

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [inviting, setInviting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [createdInvite, setCreatedInvite] = useState(null);
  const load = async () => {
    const [u, i, t] = await Promise.all([
      api.get('/admin/users'),
      api.get('/admin/invitations'),
      api.get('/teams').catch(() => ({ data: { teams: [] } })),
    ]);
    setItems(u.data.users);
    setInvitations(i.data.invitations);
    setTeams(t.data.teams || []);
  };
  useEffect(() => { load(); }, []);
  const newInvite = () => setInviting({ email: '', name: '', role: 'ventas' });
  const sendInvite = async () => {
    try {
      const { data } = await api.post('/admin/users/invite', inviting);
      setInviting(null);
      setCreatedInvite({ ...data.invitation, email_sent: data.email_sent, email_reason: data.email_reason });
      load();
      if (data.email_sent) toast.success('Invitación enviada por email');
      else toast.info('Invitación creada', { description: 'Email no enviado — comparte el enlace manualmente.' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const resendInvite = async (inv) => {
    try {
      const { data } = await api.post(`/admin/invitations/${inv.id}/resend`);
      if (data.sent) toast.success('Reenviado');
      else { navigator.clipboard.writeText(data.invite_link); toast.info('Enlace copiado', { description: 'Email no enviado.' }); }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const revokeInvite = async (inv) => {
    if (!confirm('Revocar invitación?')) return;
    await api.delete(`/admin/invitations/${inv.id}`); load(); toast.success('Revocada');
  };
  const saveEdit = async () => {
    try {
      const p = {
        name: editing.name,
        role: editing.role,
        disabled: editing.disabled,
        team_ids: editing.team_ids || [],
        skills: (editing.skills_text || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (editing.password) p.password = editing.password;
      await api.patch(`/admin/users/${editing.id}`, p);
      toast.success('Guardado'); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const toggleUserTeam = (tid) => {
    setEditing((prev) => {
      const set = new Set(prev.team_ids || []);
      if (set.has(tid)) set.delete(tid);
      else set.add(tid);
      return { ...prev, team_ids: [...set] };
    });
  };
  const del = async (u) => { if (!confirm(`Eliminar ${u.email}?`)) return; await api.delete(`/admin/users/${u.id}`); load(); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copiado'); };

  const pending = invitations.filter((i) => !i.used_at || (i.used_at && !i.used_at.match(/^\d{4}-/) === false && i.used_at.endsWith('_invalidated') === false));
  const activeInvites = invitations.filter((i) => !i.used_at);

  return (
    <div>
      <PageHeader title="Usuarios y roles" description="Equipo interno. Las invitaciones se envían por email; cada empleado define su propia contraseña." actions={<button data-testid="users-invite" onClick={newInvite} className="btn-primary"><Plus className="h-4 w-4" /> Invitar usuario</button>} />

      {activeInvites.length > 0 && (
        <div className="surface p-4 mb-4">
          <h3 className="font-display font-semibold flex items-center gap-2 mb-2"><Clock className="h-4 w-4 text-[hsl(var(--warning))]" /> Invitaciones pendientes ({activeInvites.length})</h3>
          <div className="space-y-2">
            {activeInvites.map((inv) => (
              <div key={inv.id} data-testid="invitation-row" className="flex items-center gap-2 p-2 border border-border rounded-[10px]">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{inv.name} <span className="text-xs text-muted-foreground">· {inv.email}</span></div>
                  <div className="text-xs text-muted-foreground">{ROLE_LABELS[inv.role]} · enviada {fmtRelative(inv.created_at)}</div>
                </div>
                <button onClick={() => resendInvite(inv)} className="btn-ghost h-8 px-2 text-xs"><RefreshCw className="h-3 w-3" /> Reenviar</button>
                <button onClick={() => revokeInvite(inv)} className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Shield} title="Sin usuarios activos" description="Invita al primer miembro de tu equipo." cta={<button onClick={newInvite} className="btn-primary">Invitar</button>} />
      ) : (
        <div className="surface">
          <table className="w-full text-sm"><thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="px-3 py-2">Nombre</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Rol</th><th className="px-3 py-2">Equipos</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2 text-right"></th></tr></thead>
            <tbody>{items.map((u) => (
              <tr key={u.id} data-testid="user-row" className="h-10 border-b border-border/70 hover:bg-accent/60">
                <td className="px-3">{u.name}</td>
                <td className="px-3 text-muted-foreground">{u.email}</td>
                <td className="px-3"><span className={`rounded-full px-2 py-0.5 text-[10px] ${ROLE_BADGE_CLASS[u.role]}`}>{ROLE_LABELS[u.role]}</span></td>
                <td className="px-3 text-xs text-muted-foreground">
                  {(u.team_ids || []).length
                    ? (u.team_ids || []).map((tid) => teams.find((t) => t.id === tid)?.name || tid).join(', ')
                    : '—'}
                </td>
                <td className="px-3 text-xs text-muted-foreground">{u.disabled ? 'Deshabilitado' : 'Activo'}</td>
                <td className="px-3 text-right"><button onClick={() => setEditing({ ...u, team_ids: u.team_ids || [], skills_text: (u.skills || []).join(', '), password: '' })} className="btn-ghost h-7 px-2 text-xs">Editar</button><button onClick={() => del(u)} className="btn-ghost h-7 w-7 p-0 text-[hsl(var(--danger))]"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}</tbody></table>
        </div>
      )}

      {inviting && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setInviting(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl font-semibold">Invitar usuario</h2><button onClick={() => setInviting(null)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button></div>
            <p className="text-xs text-muted-foreground mb-3">Se enviará un email con un enlace para que defina su contraseña. Usa tu SMTP si está configurado, sino el SMTP global.</p>
            <div className="space-y-3">
              <label>Nombre<input value={inviting.name} onChange={(e) => setInviting({ ...inviting, name: e.target.value })} className="input mt-1" /></label>
              <label>Email<input type="email" value={inviting.email} onChange={(e) => setInviting({ ...inviting, email: e.target.value })} className="input mt-1" /></label>
              <label>Rol<select value={inviting.role} onChange={(e) => setInviting({ ...inviting, role: e.target.value })} className="input mt-1">{ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</select></label>
            </div>
            <div className="flex justify-end gap-2 mt-4"><button onClick={() => setInviting(null)} className="btn-ghost">Cancelar</button><button data-testid="invite-send" onClick={sendInvite} className="btn-primary"><Mail className="h-4 w-4" /> Enviar invitación</button></div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl font-semibold">Editar usuario</h2><button onClick={() => setEditing(null)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button></div>
            <div className="space-y-3">
              <label>Nombre<input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input mt-1" /></label>
              <label>Rol<select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="input mt-1">{ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</select></label>
              <label>Resetear contraseña (opcional)<input type="password" minLength={8} value={editing.password || ''} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="input mt-1" /></label>
              {teams.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Equipos / sucursales</div>
                  <div className="max-h-36 overflow-y-auto border border-border rounded-[10px] p-2 space-y-1">
                    {teams.map((t) => (
                      <label key={t.id} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={(editing.team_ids || []).includes(t.id)} onChange={() => toggleUserTeam(t.id)} />
                        {t.name}
                        <span className="text-[10px] text-muted-foreground">{t.kind === 'branch' ? 'sucursal' : 'equipo'}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <label>Skills (separados por coma)
                <input
                  className="input mt-1 text-sm"
                  value={editing.skills_text || ''}
                  onChange={(e) => setEditing({ ...editing, skills_text: e.target.value })}
                  placeholder="ventas, soporte, facturacion"
                />
              </label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.disabled} onChange={(e) => setEditing({ ...editing, disabled: e.target.checked })} /> Deshabilitado</label>
            </div>
            <div className="flex justify-end gap-2 mt-4"><button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button><button onClick={saveEdit} className="btn-primary"><Save className="h-4 w-4" /> Guardar</button></div>
          </div>
        </div>
      )}

      {createdInvite && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setCreatedInvite(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl font-semibold">Invitación enviada</h2><button onClick={() => setCreatedInvite(null)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button></div>
            <p className="text-sm">Invitación enviada a <b>{createdInvite.email}</b>.</p>
            <div className="mt-3 surface p-3 border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5">
              <div className="text-xs font-medium mb-1">{createdInvite.email_sent ? '✓ Email enviado' : '⚠ Email no enviado (' + (createdInvite.email_reason || 'SMTP no configurado') + ')'}. Enlace de respaldo:</div>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 font-mono text-xs break-all bg-secondary/40 p-2 rounded-[8px]">{createdInvite.invite_link}</code>
                <button onClick={() => copy(createdInvite.invite_link)} className="btn-ghost h-8 w-8 p-0"><Copy className="h-4 w-4" /></button>
              </div>
            </div>
            <button onClick={() => setCreatedInvite(null)} className="btn-primary w-full mt-4">Listo</button>
          </div>
        </div>
      )}
    </div>
  );
}
